import React, { useRef, useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Badge } from '@/components/ui/badge';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const CATEGORIES = [
  { value: 'All', label: 'All', color: 'bg-primaryDark text-white' },
  { value: 'outdoors', label: 'Outdoors', color: 'bg-categoryOutdoors-bg text-categoryOutdoors-text' },
  { value: 'crafts', label: 'Crafts', color: 'bg-categoryCrafts-bg text-categoryCrafts-text' },
  { value: 'cooking', label: 'Cooking', color: 'bg-categorySocial-bg text-categorySocial-text' },
  { value: 'painting', label: 'Painting', color: 'bg-categoryPainting-bg text-categoryPainting-text' },
  { value: 'social', label: 'Social', color: 'bg-categorySocial-bg text-categorySocial-text' },
  { value: 'exercise', label: 'Exercise', color: 'bg-categoryOutdoors-bg text-categoryOutdoors-text' },
  { value: 'learning', label: 'Learning', color: 'bg-categoryCrafts-bg text-categoryCrafts-text' },
  { value: 'entertainment', label: 'Entertainment', color: 'bg-categoryPainting-bg text-categoryPainting-text' },
  { value: 'relaxation', label: 'Relaxation', color: 'bg-categorySocial-bg text-categorySocial-text' },
];

export const CategoryTags = ({ selected, onSelect }) => {
  const scrollContainerRef = useRef(null);
  const [showLeftChevron, setShowLeftChevron] = useState(false);
  const [showRightChevron, setShowRightChevron] = useState(false);

  const checkScrollButtons = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftChevron(scrollLeft > 0);
    setShowRightChevron(scrollLeft < scrollWidth - clientWidth - 10);
  };

  useEffect(() => {
    checkScrollButtons();
    window.addEventListener('resize', checkScrollButtons);
    return () => window.removeEventListener('resize', checkScrollButtons);
  }, []);

  const scroll = (direction) => {
    if (!scrollContainerRef.current) return;
    
    const scrollAmount = 200;
    const newScrollLeft = direction === 'left' 
      ? scrollContainerRef.current.scrollLeft - scrollAmount
      : scrollContainerRef.current.scrollLeft + scrollAmount;
    
    scrollContainerRef.current.scrollTo({
      left: newScrollLeft,
      behavior: 'smooth'
    });
  };

  return (
    <div className="relative">
      {/* Left Chevron - Desktop Only */}
      {showLeftChevron && (
        <button
          onClick={() => scroll('left')}
          className="hidden md:flex absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white border border-cardBorder hover:bg-surface rounded-full shadow-sm cursor-pointer transition-colors"
        >
          <ChevronLeft className="w-4 h-4 text-grayMeta" />
        </button>
      )}

      {/* Categories Container */}
      <div 
        ref={scrollContainerRef}
        onScroll={checkScrollButtons}
        className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 md:px-10" 
        style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
      >
        <style>{`
          .flex::-webkit-scrollbar {
            display: none;
          }
        `}</style>
        {CATEGORIES.map((cat) => (
          <Badge
            key={cat.value}
            data-testid={`category-${cat.value.toLowerCase()}`}
            onClick={() => onSelect(cat.value)}
            className={`cursor-pointer px-3 py-2 text-xs font-semibold rounded-lg border whitespace-nowrap transition-all ${
              selected === cat.value
                ? `${cat.color} border-transparent`
                : 'bg-white text-grayMeta border-cardBorder hover:bg-chipActive hover:border-chipActive-border'
            }`}
          >
            {cat.label}
          </Badge>
        ))}
      </div>

      {/* Right Chevron - Desktop Only */}
      {showRightChevron && (
        <button
          onClick={() => scroll('right')}
          className="hidden md:flex absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 items-center justify-center bg-white border border-cardBorder hover:bg-surface rounded-full shadow-sm cursor-pointer transition-colors"
        >
          <ChevronRight className="w-4 h-4 text-grayMeta" />
        </button>
      )}
    </div>
  );
};

export { CATEGORIES };
